import { Box, Button, Token, Typography } from '@screentone/core';

import { usePublicationSettings } from 'hooks/publication-settings';
import type { AllessehSummariesResponse } from 'hooks/useAllessehCollectionSummariesQuery';
import type { AllessehContentItemAttributes } from 'hooks/useAllessehContentQuery';
import type { Article } from '../Debug.utils';
import { formatDate, getAllessehAdminUrl } from '../Debug.utils';

const ArticleCard = ({
  articleId,
  rawArticleData,
  collectionSummaryData,
  embargoArticleData,
  isMissing = false,
  index
}: {
  articleId: string;
  rawArticleData: Article;
  collectionSummaryData: AllessehSummariesResponse;
  embargoArticleData?: AllessehContentItemAttributes | null;
  isMissing?: boolean;
  index?: number;
}) => {
  const { data: publicationSettings } = usePublicationSettings();
  let articleData = null;
  let embargoDatetime = null;

  // First, check if embargo article data was provided
  if (embargoArticleData) {
    articleData = {
      id: articleId,
      headline: embargoArticleData.headline?.text ?? 'No headline',
      updated_datetime_utc: embargoArticleData.updated_datetime_utc,
      status: embargoArticleData.content_status || 'embargo',
      product: embargoArticleData.product || 'Unknown',
      type: embargoArticleData.type || 'article',
      source_url: embargoArticleData.source_url
    };
    embargoDatetime = embargoArticleData.embargo_datetime_utc;
  }

  // Next, try to find the article in the summary collection's included array
  if (!articleData) {
    const includedItem = collectionSummaryData.included.find((inc) => inc.id === articleId);
    if (includedItem?.attributes.data.attributes) {
      const attrs = includedItem.attributes.data.attributes;
      articleData = {
        id: articleId,
        headline: attrs.headline?.text ?? 'No headline',
        updated_datetime_utc: attrs.updated_datetime_utc,
        status: attrs.content_status,
        product: attrs.product,
        type: attrs.type,
        source_url: attrs.source_url
      };
    }
  }

  // If not found in summary, use raw data as fallback
  if (!articleData) {
    const headline: string = rawArticleData.headline ?? rawArticleData.title ?? 'No headline';
    articleData = {
      id: articleId,
      headline,
      updated_datetime_utc: null,
      status: rawArticleData.status ?? 'Unknown',
      product: rawArticleData.product ?? 'Unknown',
      type: rawArticleData.type,
      source_url: rawArticleData.source_url
    };
  }

  // Build Allesseh admin URL (check for embargo status)
  const isEmbargo = articleData.status === 'embargo' || rawArticleData.content_status === 'embargo';
  const adminUrl = getAllessehAdminUrl(
    articleData.id,
    articleData.type === 'collection' ? 'collection' : 'article',
    isEmbargo
  );

  const boxStyle: Record<string, string> = {};
  if (isMissing) {
    boxStyle.backgroundColor = 'var(--st-color-lava-10)';
    boxStyle.color = 'var(--st-color-lava-70)';
  }

  return (
    <Box style={boxStyle} key={articleId} margin={{ bottom: 'xs' }} padding={{ all: 'xs' }} id={`article-${articleId}`}>
      <Typography size="sm" color={isMissing ? 'lava' : 'asphalt'}>
        <strong>
          {index !== undefined && `${index}. `}
          {isMissing ? 'ERROR RESOLVING DATA: Article not found in Allesseh' : articleData.headline}
        </strong>
      </Typography>
      <Typography size="sm" color={isMissing ? 'lava' : 'asphalt'}>
        Product: {articleData.product} | Status: {articleData.status} | Type: {articleData.type}
        {articleData.updated_datetime_utc && (
          <>
            {' '}
            | Updated:{' '}
            {formatDate(
              articleData.updated_datetime_utc,
              publicationSettings?.publicationSetting.preferredTimezone ?? null
            )}
          </>
        )}
        {embargoDatetime && (
          <>
            {' '}
            |{' '}
            <Token color="lava">
              Embargo: {formatDate(embargoDatetime, publicationSettings?.publicationSetting.preferredTimezone ?? null)}
            </Token>
          </>
        )}
      </Typography>
      <Typography size="sm" color={isMissing ? 'lava' : 'asphalt'}>
        ID:{' '}
        {adminUrl ? (
          <Button tertiary componentEl="a" href={adminUrl} target="_blank" rel="noopener noreferrer">
            {articleData.id}
          </Button>
        ) : (
          articleData.id
        )}
      </Typography>
      {/* Display parameters if present and non-empty */}
      {rawArticleData.parameters && Array.isArray(rawArticleData.parameters) && rawArticleData.parameters.length > 0 && (
        <>
          {rawArticleData.parameters.map((param, idx) => {
            // Format the value based on its type
            let formattedValue: string;
            if (param.value === undefined) {
              formattedValue = '(not set)';
            } else if (typeof param.value === 'object' && param.value !== null) {
              formattedValue = JSON.stringify(param.value);
            } else if (typeof param.value === 'boolean') {
              formattedValue = param.value ? 'true' : 'false';
            } else if (param.value === null) {
              formattedValue = '';
            } else {
              formattedValue = String(param.value);
            }

            return (
              <Typography key={idx} size="sm" color={isMissing ? 'lava' : 'asphalt'}>
                <strong>{param.name}:</strong> {formattedValue}
              </Typography>
            );
          })}
        </>
      )}
    </Box>
  );
};

export default ArticleCard;
